<template>
  <div>
    <vue2Dropzone
      id="dropzone"
      ref="dropzoneForm"
      :options="dropZoneOptions"
      :use-custom-slot="true"
      @vdropzone-sending="onSending"
      @vdropzone-complete="onComplete"
      @vdropzone-success="onSuccess"
      @vdropzone-removed-file="onRemoved"
      @vdropzone-error="onError"
      @vdropzone-drop="emitEvent('drop')"
      @vdropzone-dragover="emitEvent('dragover')"
      @vdropzone-dragleave="emitEvent('dragleave')"
    >
      <!-- drop The user dropped something onto the dropzone
        dragstart	The user started to drag anywhere
        dragend	Dragging has ended
        dragenter	The user dragged a file onto the Dropzone
        dragover	The user is dragging a file over the Dropzone
        dragleave -->
      <div class="nx-files">
        <!-- <span class="mr-2 my-2 text-secondary"
                      >{{ $t("global.attach-nx-files") }}:</span
                    > -->
        <div class="nx-files-drop-area" v-if="!files.length">
          <div class="nx-files-drop-area-inner">
            <div>{{ $t("global.drop-files") }}</div>
          </div>
        </div>

        <div class="nx-dropzone-previews" :id="previewId"></div>

        <div class="nx-dropzone-actions text-center">
          <div v-if="!sending">
            <a :id="buttonId" :loading="sending" href="javascript:void(0)">
              {{ $t("global.browse-files") }}
            </a>
            <template v-if="enableCancel"
              >or
              <a href="javascript:void(0)" @click="emitEvent('cancel')">{{
                $t("global.cancel")
              }}</a></template
            >
          </div>
          <div v-else>{{ $t("global.attaching") }}</div>
        </div>
      </div>
    </vue2Dropzone>
    <div class="text-danger" v-if="errorMsg">
      {{ errorMsg }}
    </div>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
// import Btn from "@/components/display/Btn";
import store from "@/store";
import { uniqueId } from "lodash-es";
import vue2Dropzone from "vue2-dropzone";

const defaultDropzonePreviewTemplate = `
<div class="nx-dropzone-template">
  <div class="attachment-thumbnail">
    <img data-dz-thumbnail />
  </div>
  <div class="attachment-name" data-dz-name></div>
  <a class="attachment-remove" data-dz-remove>×</a>
</div>
`;

export default {
  name: "Dropzone2",
  components: {
    vue2Dropzone,
    // Btn,
  },
  props: {
    value: Array,
    id: String,
    enableCancel: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit, refs, root }) {
    const previewId = uniqueId("nx-dropzone-preview-");
    const buttonId = uniqueId("nx-dropzone-button-");
    // const idCalc = computed(() => {
    //   return props.id || uniqueId("nx-dropzone-");
    // });

    const sending = ref(false);

    const disable = computed(() => {
      return sending.value;
    });

    // config for dropzone library
    const dropZoneOptions = {
      url: `${process.env.VUE_APP_API_URL || ""}/attachment/new`, // nocworx endpoint we send attachments to
      clickable: `#${buttonId}`, // id of the button that triggers an browse/upload
      maxFilesize: 64,
      thumbnailWidth: 50,
      thumbnailHeight: 50,
      timeout: 600000,
      // addRemoveLinks: true,
      dictCancelUpload: root.$t("global.cancel"), // what to show on the canel button that appears during upload
      dictRemoveFile: "&times;", // what character(s) to use for the remove button on the upload preview thumbnail
      previewsContainer: `#${previewId}`, // identifier for which element to show the upload previews in
      previewTemplate: defaultDropzonePreviewTemplate,
      headers: { Authorization: `Bearer ${store.state.auth.token.fullString}` },
    };

    const files = ref([]);
    const errorMsg = ref(null);

    const onSending = (file) => {
      sending.value = true;
      errorMsg.value = null;

      const existingFile = files.value.find((f) => f.name === file.name);

      // if file has bee selected already, or if the new attachment has a duplicate file name, don't allow upload
      if (existingFile) {
        // inform dropzone that the file should not be included
        refs.dropzoneForm.removeFile(file);
        // display error
        errorMsg.value = root.$t("global.could-not-upload-duplicate-file-name", {
          name: existingFile.name,
        });
        return;
      }

      emit("sending", true);
    };

    const onComplete = () => {
      sending.value = false;
      emit("complete", false);
    };

    // should display a generic error if dropzone fails to upload the file
    const onError = (file) => {
      sending.value = false;
      const errorMesssage = root.$t("global.could-not-upload-file", { name: file.name });
      errorMsg.value = errorMesssage;
      // tell dropzone to remove the file from the list
      refs.dropzoneForm.removeFile(file);
    };

    const onSuccess = (file, attachment) => {
      sending.value = false;
      // save the attachment in a list
      // attachment name is identical to file name - that's how we'll re-associate attachments to file
      files.value.push(attachment);
      const fileIds = files.value.map((f) => f.id);

      emit("input", fileIds);
    };

    const onRemoved = (file) => {
      // find file by its name
      const removedIndex = files.value.findIndex((f) => f.name === file.name);
      // remove from list
      files.value.splice(removedIndex, 1);

      const fileIds = files.value.map((f) => f.id);
      emit("input", fileIds);
    };

    const removeAllFiles = (cancel) => {
      refs.dropzoneForm.removeAllFiles(cancel);
    };

    const emitEvent = (eventName) => {
      emit(eventName);
    };

    return {
      dropZoneOptions,
      onSending,
      onComplete,
      onSuccess,
      onRemoved,
      onError,
      errorMsg,
      removeAllFiles,
      emitEvent,
      // idCalc,
      previewId,
      buttonId,
      disable,
      sending,
      files,
    };
  },
};
</script>
<style lang="scss">
.nx-files {
  .nx-files-drop-area {
    padding: 0.5rem;
    .nx-files-drop-area-inner {
      border-radius: 1rem;
      border-style: dashed;
      border-width: 1px;
      padding: 1rem;
      font-size: 0.875rem;
      text-align: center;
    }
  }
  // .nx-dropzone-browser-btn {
  // }
  .nx-dropzone-previews {
    .nx-dropzone-template {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 1rem;
      .attachment-remove {
        font-weight: bold;
        cursor: pointer;
      }
      .attachment-name {
        margin-right: 1rem;
      }
      .attachment-thumbnail {
        margin-right: 1rem;
        height: 50px;
        width: 50px;
        img {
          height: 100%;
          width: 100%;
        }
      }
    }
  }
}
</style>
